import React, { useEffect } from "react";
import PropTypes from "prop-types";
import * as Mui from "@mui/material";
import { LockOutlined } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  designColors,
  designFontFamily,
  designShell,
  designTheme,
  designType,
} from "services/constants/designTokens";
import { GlobalBreadcrumb } from "components/DesignSystem/sharedUiPatterns";
import { setFilterApi } from "services/Redux/userManagament";
import { Loader } from "components/Loader";
import { MonitoringPolicy } from "./CheckinPolicy";
import { SiteSettings } from "./GeoFencedSites/SiteSettings";
import { useTeamSettingsAccess } from "./settingsAccess";

// Team Workspace > Settings. Reached from the sidebar's Settings flyout
// (routes.js); each flyout row is its own page, same as Administration >
// Organisation. Both screens are standard lists (CustomFilter toolbar, saved
// views, sticky table) with GlobalSidePanel drawers.
const SCREENS = {
  policy: {
    title: "Check-in Policy",
    subtitle:
      "Define rules for employee check-ins and work session automation.",
    subModuleName: "Check in Policy",
    Component: MonitoringPolicy,
  },
  site: {
    title: "Geo-Fenced Sites",
    subtitle: "Set up and manage geo-fenced sites for location-based policies.",
    subModuleName: "Geo Fenced Sites",
    Component: SiteSettings,
  },
};

const TeamWorkspaceSettings = ({ screen }) => {
  const { title, subtitle, subModuleName, Component } = SCREENS[screen];
  const dispatch = useDispatch();
  const history = useHistory();
  const access = useTeamSettingsAccess(subModuleName);

  // Both lists reload off this flag; a direct visit or refresh never goes
  // through the sidebar click that normally sets it.
  useEffect(() => {
    dispatch(setFilterApi(true));
  }, [dispatch, screen]);

  return (
    <Mui.ThemeProvider theme={designTheme}>
      {/* Same page chrome as the other Team Workspace / Administration list
          pages. The page itself scrolls, so each list's toolbar, table header
          and footer can stick under the app bar. */}
      <Mui.Box
        sx={{
          ...designShell.pageSurface,
          fontFamily: designFontFamily,
          p: { xs: 2, md: 3 },
        }}
      >
        <Mui.Stack spacing={2}>
          <GlobalBreadcrumb
            trail={[
              {
                label: "Team Workspace",
                onClick: () => history.push("/admin/teamzone/home"),
              },
              "Settings",
              { label: title },
            ]}
          />
          <Mui.Box>
            <Mui.Typography component="h1" sx={designType.pageTitle}>
              {title}
            </Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              {subtitle}
            </Mui.Typography>
          </Mui.Box>
          {access.loading ? (
            <Loader info="Loading..." />
          ) : access.canView ? (
            <Component moduleDetails={access.moduleDetails} />
          ) : (
            <Mui.Box
              sx={{
                ...designShell.sectionCard,
                p: { xs: 4, md: 6 },
                textAlign: "center",
              }}
            >
              <LockOutlined
                sx={{ fontSize: 56, color: designColors.tealDark, mb: 1.5 }}
              />
              <Mui.Typography sx={designType.panelTitle}>
                Access Restricted
              </Mui.Typography>
              <Mui.Typography sx={{ ...designType.helperText, mt: 0.5 }}>
                You do not have permission to view {title}. Please contact
                your administrator.
              </Mui.Typography>
            </Mui.Box>
          )}
        </Mui.Stack>
      </Mui.Box>
    </Mui.ThemeProvider>
  );
};

TeamWorkspaceSettings.propTypes = {
  screen: PropTypes.oneOf(Object.keys(SCREENS)).isRequired,
};

export const TeamCheckInPolicy = () => <TeamWorkspaceSettings screen="policy" />;
export const TeamGeoFencedSites = () => <TeamWorkspaceSettings screen="site" />;
